// Two conditions that are one filter.
//
// Before multi-part chip filters were written in parentheses, a JSON filter on
// a text column declared as JSON landed in the WHERE bare:
// `<validity guard> AND <test>` (`json_valid(meta) = 1 AND CAST(json_extract(…)
// AS TEXT) = 'x'`). The resolver reads that as two conditions, so the guard
// showed as its own pill that could be edited or removed alone.
//
// `foldConditionPairs` merges every two adjacent AND-joined conditions whose
// source text the caller recognises as one filter into ONE condition — a leaf
// over both in the WHERE / HAVING tree, one fragment in the flat list. The
// resolver service applies it (when created with `foldConditionPair`) to the
// structure it hands out AND to the structure it plans edits on, so every view
// shows one pill and a fragment index / tree path means the same pair on both
// sides: modify rewrites both, remove drops both. An AND pair keeps its meaning
// wherever an edit puts it next to an OR (AND binds tighter).

import type { SqlRange } from '../../sql-grammar/model';
import type { BooleanFragment, BooleanNode, QueryStructure } from '../types';

type IsPair = (text: string) => boolean;

function spanOf(a: SqlRange, b: SqlRange): SqlRange {
  return {
    startOffset: a.startOffset,
    startLine: a.startLine,
    startColumn: a.startColumn,
    endOffset: b.endOffset,
    endLine: b.endLine,
    endColumn: b.endColumn,
  };
}

/** The pair's merged range when `source` over `a … b` is one filter, else null. */
function pairRange(a: SqlRange, b: SqlRange, source: string, isPair: IsPair): SqlRange | null {
  const range = spanOf(a, b);
  return isPair(source.slice(range.startOffset, range.endOffset)) ? range : null;
}

function foldTree(node: BooleanNode, source: string, isPair: IsPair, root: boolean): BooleanNode {
  if (node.kind === 'leaf') return node;
  const children = node.children.map((child) => foldTree(child, source, isPair, false));
  const out: BooleanNode[] = [];
  // A parenthesised (or negated) group of exactly the pair stays a group —
  // `foldWholeGroups` shows it whole; the pair is folded only among siblings.
  const foldHere = node.connector === 'AND' && !((node.explicit || node.negated) && children.length === 2);
  for (let i = 0; i < children.length; i++) {
    const next = children[i + 1];
    const range = foldHere && next ? pairRange(children[i]!.range, next.range, source, isPair) : null;
    if (range) {
      out.push({ kind: 'leaf', range, text: source.slice(range.startOffset, range.endOffset), negated: false });
      i += 1;
    } else {
      out.push(children[i]!);
    }
  }
  // An implicit (precedence) group left with one member is that member.
  if (!root && !node.explicit && !node.negated && out.length === 1) return out[0]!;
  const same = out.length === node.children.length && out.every((c, i) => c === node.children[i]);
  return same ? node : { ...node, children: out };
}

function foldFlat(fragments: readonly BooleanFragment[], source: string, isPair: IsPair): readonly BooleanFragment[] {
  const out: BooleanFragment[] = [];
  for (let i = 0; i < fragments.length; i++) {
    const frag = fragments[i]!;
    const next = fragments[i + 1];
    const range = next?.leadingConnector === 'AND' ? pairRange(frag.range, next.range, source, isPair) : null;
    if (range) {
      out.push({
        range,
        text: source.slice(range.startOffset, range.endOffset),
        ...(frag.leadingConnector ? { leadingConnector: frag.leadingConnector } : {}),
      });
      i += 1;
    } else {
      out.push(frag);
    }
  }
  return out.length === fragments.length ? fragments : out;
}

/**
 * `structure` with each two adjacent AND-joined WHERE / HAVING conditions whose
 * `source` text `isPair` accepts merged into one condition, in the tree and the
 * flat list alike (the flat list is the precedence-transparent view of the
 * tree, so AND-adjacent fragments are always siblings of one AND group and
 * both views fold the same pairs). Unchanged when nothing folds.
 */
export function foldConditionPairs(structure: QueryStructure, source: string, isPairText: IsPair): QueryStructure {
  const { where, whereTree, having, havingTree } = structure;
  // The tree and the flat list ask about the same pairs — answer each once.
  const seen = new Map<string, boolean>();
  const isPair: IsPair = (text) => {
    let answer = seen.get(text);
    if (answer === undefined) seen.set(text, (answer = isPairText(text)));
    return answer;
  };
  const folded: QueryStructure = {
    ...structure,
    ...(where ? { where: foldFlat(where, source, isPair) } : {}),
    ...(whereTree ? { whereTree: foldTree(whereTree, source, isPair, true) } : {}),
    ...(having ? { having: foldFlat(having, source, isPair) } : {}),
    ...(havingTree ? { havingTree: foldTree(havingTree, source, isPair, true) } : {}),
  };
  const changed =
    folded.where !== where || folded.whereTree !== whereTree || folded.having !== having || folded.havingTree !== havingTree;
  return changed ? folded : structure;
}
